/**
 * The scanner's status as the browser reads it, once a tab.
 *
 * `/api/scanner/status` is the nav pill's poll: every 30 s in the session
 * while the tab is in view, and once at mount. The pill is mounted twice in
 * the top nav (the bar and the compact chrome), and each mount fetched on
 * its own, so every tab asked twice at mount and twice every 30 s: 304
 * requests in the session of 2026-10-19 for 152 readings, on the route the
 * API answers from twelve synchronous statements (`readScannerStatus`).
 *
 * Here one read serves every caller for `STATUS_SHARE_MS`, a read in flight
 * is shared with whoever asks meanwhile, and the pill's two mounts therefore
 * make one request. A failed read is not kept. Pure apart from `fetch`, which
 * a check hands in.
 */
export const STATUS_SHARE_MS = 25_000;

export type StatusReader<T> = {
  /** The status: the one read for `STATUS_SHARE_MS`, or a new read. Throws as the read throws. */
  read(): Promise<T>;
  /** For a check: how many reads were made. */
  reads(): number;
};

export function createStatusReader<T>(fetchStatus: () => Promise<T>, now: () => number = Date.now, shareMs = STATUS_SHARE_MS): StatusReader<T> {
  let held: { at: number; value: T } | null = null;
  let inFlight: Promise<T> | null = null;
  let reads = 0;
  return {
    read() {
      if (held && now() - held.at < shareMs) return Promise.resolve(held.value);
      if (inFlight) return inFlight;
      reads += 1;
      inFlight = fetchStatus()
        .then((value) => {
          held = { at: now(), value };
          return value;
        })
        .finally(() => {
          inFlight = null;
        });
      return inFlight;
    },
    reads: () => reads,
  };
}

async function fetchScannerStatus<T>(): Promise<T> {
  const r = await fetch("/api/scanner/status");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return (await r.json()) as T;
}

let browserReader: StatusReader<unknown> | null = null;

/** The tab's one reader of `/api/scanner/status`. */
export function scannerStatusReader<T>(): StatusReader<T> {
  if (!browserReader) browserReader = createStatusReader<unknown>(() => fetchScannerStatus<unknown>());
  return browserReader as StatusReader<T>;
}
